import { useState } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { useClientePedidos } from '../../hooks/useClientePedidos';
import {
  Package,
  Search,
  Calendar,
  Filter,
  Download,
  CheckCircle,
  XCircle,
  ChevronDown,
  Loader2,
  AlertCircle,
} from 'lucide-react';

export const HistorialPedidosPage: React.FC = () => {
  const { theme } = useTheme();
  const { pedidos, loading, error } = useClientePedidos();
  const [busqueda, setBusqueda] = useState('');
  const [filtroFecha, setFiltroFecha] = useState('todos');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [pedidoExpandido, setPedidoExpandido] = useState<string | null>(null);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  // Solo pedidos finalizados
  const pedidosHistorial = pedidos.filter((p) =>
    ['DELIVERED', 'CANCELLED'].includes(p.estado)
  );

  const filtrarPorFecha = (fechaStr: string) => {
    const fechaPedido = new Date(fechaStr);
    const ahora = new Date();

    switch (filtroFecha) {
      case 'este_mes':
        return (
          fechaPedido.getMonth() === ahora.getMonth() &&
          fechaPedido.getFullYear() === ahora.getFullYear()
        );
      case 'ultimos_3_meses':
        const hace3Meses = new Date();
        hace3Meses.setMonth(hace3Meses.getMonth() - 3);
        return fechaPedido >= hace3Meses;
      case 'este_año':
        return fechaPedido.getFullYear() === ahora.getFullYear();
      default:
        return true;
    }
  };

  const pedidosFiltrados = pedidosHistorial.filter((pedido) => {
    const matchBusqueda =
      pedido.numeroPedido.toLowerCase().includes(busqueda.toLowerCase()) ||
      pedido.items.some((item) =>
        item.nombreProducto.toLowerCase().includes(busqueda.toLowerCase())
      );
    const matchEstado = filtroEstado === 'todos' || pedido.estado === filtroEstado;
    const matchFecha = filtrarPorFecha(pedido.fechas.creadoEn);
    return matchBusqueda && matchEstado && matchFecha;
  });

  const estadisticas = {
    total: pedidosHistorial.length,
    entregados: pedidosHistorial.filter((p) => p.estado === 'DELIVERED').length,
    cancelados: pedidosHistorial.filter((p) => p.estado === 'CANCELLED').length,
    totalGastado: pedidosHistorial
      .filter((p) => p.estado === 'DELIVERED')
      .reduce((sum, p) => sum + p.totales.total, 0),
  };

  const getEstadoIcon = (estado: string) => {
    return estado === 'DELIVERED' ? (
      <CheckCircle className="w-4 h-4" />
    ) : (
      <XCircle className="w-4 h-4" />
    );
  };

  const getEstadoColor = (estado: string) => {
    return estado === 'DELIVERED'
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-red-100 text-red-800 border-red-200';
  };

  const getEstadoLabel = (estado: string) => {
    if (estado === 'DELIVERED') return 'Entregado';
    if (estado === 'CANCELLED') return 'Cancelado';
    return estado;
  };

  return (
    <div className="p-6 flex flex-col gap-4 h-[calc(100vh-5rem)]">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-shrink-0">
        <div>
          <h1 className={`text-lg font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Historial de Pedidos
          </h1>
          <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Revisa tus pedidos anteriores
          </p>
        </div>
        <button
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${
            theme === 'dark'
              ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
              : 'border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Download className="w-3.5 h-3.5" />
          <span>Exportar</span>
        </button>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-shrink-0">
        <div className={`p-3 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-md ${theme === 'dark' ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
              <Package className={`w-4 h-4 ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'}`} />
            </div>
            <div>
              <p className={`text-base font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {estadisticas.total}
              </p>
              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total</p>
            </div>
          </div>
        </div>

        <div className={`p-3 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-md bg-green-100">
              <CheckCircle className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <p className={`text-base font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {estadisticas.entregados}
              </p>
              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Entregados</p>
            </div>
          </div>
        </div>

        <div className={`p-3 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-md bg-red-100">
              <XCircle className="w-4 h-4 text-red-600" />
            </div>
            <div>
              <p className={`text-base font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {estadisticas.cancelados}
              </p>
              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Cancelados</p>
            </div>
          </div>
        </div>

        <div className={`p-3 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-md ${theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
              <Package className={`w-4 h-4 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
            <div>
              <p className={`text-base font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                ${estadisticas.totalGastado.toLocaleString('es-CO')}
              </p>
              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Gastado</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros y búsqueda */}
      <div className="space-y-4 flex-shrink-0">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por número de pedido o producto..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className={`w-full pl-10 pr-4 py-3 rounded-xl border-2 transition-all duration-300 ${
                theme === 'dark'
                  ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-emerald-500'
                  : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500 focus:border-emerald-500'
              }`}
            />
          </div>
          <button
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 transition-all duration-300 ${
              theme === 'dark'
                ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-5 h-5" />
            <span>Filtros</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${mostrarFiltros ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {mostrarFiltros && (
          <div className={`p-4 rounded-xl border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Período
                </label>
                <select
                  value={filtroFecha}
                  onChange={(e) => setFiltroFecha(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="todos">Todos los períodos</option>
                  <option value="este_mes">Este mes</option>
                  <option value="ultimos_3_meses">Últimos 3 meses</option>
                  <option value="este_año">Este año</option>
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Estado
                </label>
                <select
                  value={filtroEstado}
                  onChange={(e) => setFiltroEstado(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="todos">Todos los estados</option>
                  <option value="DELIVERED">Entregado</option>
                  <option value="CANCELLED">Cancelado</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Contenido scrollable */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {/* Loading */}
        {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          <span className={`ml-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
            Cargando historial...
          </span>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className={`p-4 rounded-xl flex items-center gap-3 ${
          theme === 'dark' ? 'bg-red-900/30 border border-red-700' : 'bg-red-50 border border-red-200'
        }`}>
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className={theme === 'dark' ? 'text-red-300' : 'text-red-700'}>{error}</p>
        </div>
      )}

        {/* Lista de pedidos */}
        {!loading && !error && (
          <div className="space-y-4">
          {pedidosFiltrados.map((pedido) => (
            <div
              key={pedido.id}
              className={`border rounded-xl transition-all duration-300 ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}
            >
              <div
                className="p-5 cursor-pointer"
                onClick={() => setPedidoExpandido(pedidoExpandido === pedido.id ? null : pedido.id)}
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${pedido.estado === 'DELIVERED' ? 'bg-green-100' : 'bg-red-100'}`}>
                      {getEstadoIcon(pedido.estado)}
                    </div>
                    <div>
                      <h3 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {pedido.numeroPedido}
                      </h3>
                      <div className="flex items-center gap-4 mt-1">
                        <p className={`text-sm flex items-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(pedido.fechas.creadoEn).toLocaleDateString()}
                        </p>
                        <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${getEstadoColor(pedido.estado)}`}>
                          {getEstadoIcon(pedido.estado)}
                          <span>{getEstadoLabel(pedido.estado)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between lg:justify-end gap-4">
                    <div className="text-right">
                      <p className={`font-semibold text-lg ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        ${pedido.totales.total.toLocaleString('es-CO')}
                      </p>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {pedido.items.length} producto{pedido.items.length !== 1 ? 's' : ''}
                      </p>
                    </div>

                  </div>
                </div>

                {/* Detalles expandidos */}
                {pedidoExpandido === pedido.id && (
                  <div className={`mt-5 pt-5 border-t ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}>
                    <h4 className={`font-medium mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Productos del pedido
                    </h4>
                    <div className="space-y-2">
                      {pedido.items.map((item) => (
                        <div
                          key={item.id}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                          }`}
                        >
                          <div>
                            <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              {item.nombreProducto}
                            </p>
                            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                              Cantidad: {item.cantidad} • Precio unitario: ${item.precioUnitario.toLocaleString('es-CO')}
                            </p>
                          </div>
                          <p className={`font-semibold ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'}`}>
                            ${(item.cantidad * item.precioUnitario).toLocaleString('es-CO')}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className={`mt-4 p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700/30' : 'bg-gray-100'}`}>
                      <div className="flex justify-between text-sm">
                        <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Subtotal</span>
                        <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                          ${pedido.totales.subtotal.toLocaleString('es-CO')}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm mt-1">
                        <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Envío</span>
                        <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                          {pedido.totales.envio === 0 ? 'Gratis' : `$${pedido.totales.envio.toLocaleString('es-CO')}`}
                        </span>
                      </div>
                      {pedido.totales.descuento > 0 && (
                        <div className="flex justify-between text-sm mt-1">
                          <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Descuento</span>
                          <span className="text-green-600">
                            -${pedido.totales.descuento.toLocaleString('es-CO')}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold mt-2 pt-2 border-t border-gray-300 dark:border-gray-600">
                        <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Total</span>
                        <span className="text-emerald-600">
                          ${pedido.totales.total.toLocaleString('es-CO')}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {pedidosFiltrados.length === 0 && (
            <div className={`text-center py-12 rounded-xl border-2 border-dashed ${
              theme === 'dark' ? 'border-gray-700 text-gray-400' : 'border-gray-300 text-gray-500'
            }`}>
              <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No se encontraron pedidos</p>
              <p className="mt-1">Intenta cambiar los filtros de búsqueda</p>
            </div>
          )}
          </div>
        )}

      </div>
    </div>
  );
};
